import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Camera, CameraOff, Keyboard, Loader2 } from 'lucide-react';

// Chrome on Android settles an internal camera promise after the track has
// been stopped and rejects it with "setPhotoOptions failed". Nothing in the app
// holds that promise, so it surfaces as an unhandled rejection - in development
// as a full-screen runtime-error overlay over a scan that actually succeeded.
// The camera is already released by then (see stop()), so this one browser
// message is swallowed; every other rejection still reaches the overlay and the
// console. Registered in the capture phase so it runs before the dev overlay's
// listener on window.
export function isCameraTeardownRejection(reason) {
  return /setPhotoOptions failed/i.test(String(reason?.message || reason || ''));
}

let cameraTeardownGuardInstalled = false;
export function installCameraTeardownGuard(target = typeof window === 'undefined' ? null : window) {
  if (!target || cameraTeardownGuardInstalled) return;
  cameraTeardownGuardInstalled = true;
  target.addEventListener('unhandledrejection', (event) => {
    if (!isCameraTeardownRejection(event?.reason)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, { capture: true });
}

const fieldClass = 'min-h-11 min-w-0 flex-1 rounded-xl border border-neutral-300 bg-white px-3 text-sm text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white';

export default function CameraCodeScanner({
  label = 'Code',
  manualPlaceholder = '',
  manualPattern,
  manualInputMode = 'text',
  disabled = false,
  onDetected,
}) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const mountedRef = useRef(true);
  const scanRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [manual, setManual] = useState('');

  /**
   * Put the camera down.
   *
   * Every step is guarded on its own, because the one thing that must not
   * happen is an early failure leaving the camera running - a phone with its
   * torch of a camera light still on after the rider thinks they closed the
   * scanner is both alarming and a battery drain.
   *
   * The ordering matters on Android. Chrome raises "setPhotoOptions failed"
   * when a live track is stopped while its stream is still attached to a
   * playing video element, and again when the same track is stopped twice -
   * which this did, because releaseAllStreams() and the element's own tracks
   * are the same tracks. So: pause the sink, stop each track exactly once and
   * only while it is still live, and detach last.
   */
  const stop = (updateState = true) => {
    scanRef.current += 1;

    // On a phone with a flashlight, zxing's stop() is async: it stops scanning,
    // then switches the torch off with applyConstraints. We never turned the
    // torch on, and the track is stopped a moment later below, so Chrome rejects
    // that call with "setPhotoOptions failed". Nothing awaited it, so it became
    // an uncaught error over a scan that had succeeded. Handle it here.
    try {
      Promise.resolve(controlsRef.current?.stop?.()).catch(() => { /* torch-off on a stopped track */ });
    } catch { /* already stopped */ }
    controlsRef.current = null;

    const video = videoRef.current;
    // Detach the consumer before touching the producer.
    try { video?.pause?.(); } catch { /* not playing */ }

    const alreadyStopped = new Set();
    const stopTrack = (track) => {
      if (!track || alreadyStopped.has(track)) return;
      alreadyStopped.add(track);
      try {
        if (track.readyState !== 'ended') track.stop();
      } catch { /* a track that will not stop is already gone */ }
    };

    try { video?.srcObject?.getTracks?.().forEach(stopTrack); } catch { /* no stream */ }
    // Anything the reader opened that never reached the element. Its tracks are
    // usually the ones just stopped, and stopping an ended track is a no-op.
    try { BrowserMultiFormatReader.releaseAllStreams?.(); } catch { /* nothing tracked */ }
    try { if (video) video.srcObject = null; } catch { /* detached already */ }

    if (updateState && mountedRef.current) {
      setOpen(false);
      setStarting(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    installCameraTeardownGuard();
    return () => {
      mountedRef.current = false;
      stop(false);
    };
  }, []);

  const start = async () => {
    if (disabled || starting) return;
    const scanId = scanRef.current + 1;
    scanRef.current = scanId;
    setCameraError('');
    setOpen(true);
    setStarting(true);
    try {
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } }, audio: false },
        videoRef.current,
        (result) => {
          if (!result || scanRef.current !== scanId) return;
          const value = result.getText();
          stop();
          onDetected(value, 'QR');
        },
      );
      if (!mountedRef.current || scanRef.current !== scanId) {
        // Controls that arrived after this scan ended. Hand them to the same
        // teardown rather than repeating it here, so the camera is released the
        // one careful way in both paths.
        controlsRef.current = controls;
        stop(false);
        return;
      }
      controlsRef.current = controls;
    } catch (error) {
      if (!mountedRef.current || scanRef.current !== scanId) return;
      stop();
      if (mountedRef.current) {
        setCameraError(error?.name === 'NotAllowedError'
          ? 'Camera access was denied. Enter the code manually instead.'
          : error?.name === 'NotFoundError' || !navigator.mediaDevices?.getUserMedia
            ? 'No supported camera was found. Enter the code manually instead.'
            : 'The camera could not start. Enter the code manually instead.');
      }
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  };

  const submitManual = (event) => {
    event.preventDefault();
    const value = manual.trim();
    if (!value || (manualPattern && !manualPattern.test(value))) return;
    onDetected(value, 'MANUAL_CODE');
    setManual('');
  };

  const manualValid = Boolean(manual.trim()) && (!manualPattern || manualPattern.test(manual.trim()));

  return (
    <div className="space-y-3">
      <button type="button" disabled={disabled || starting} onClick={() => (open ? stop() : start())} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-neutral-300 px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 dark:border-neutral-700">
        {starting ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : open ? <CameraOff className="h-4 w-4" aria-hidden="true" /> : <Camera className="h-4 w-4" aria-hidden="true" />}
        {starting ? 'Starting camera…' : open ? 'Stop camera' : 'Scan QR code'}
      </button>
      <div className={open ? 'overflow-hidden rounded-xl bg-black' : 'hidden'} aria-hidden={!open}>
        <video ref={videoRef} muted playsInline className="aspect-video w-full object-cover" aria-label={`${label} camera preview`} />
      </div>
      {cameraError ? <p role="alert" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">{cameraError}</p> : null}
      <form onSubmit={submitManual} className="flex flex-col gap-2 sm:flex-row">
        <label className="sr-only" htmlFor={`manual-${label.replace(/\s+/g, '-').toLowerCase()}`}>{label}</label>
        <div className="relative min-w-0 flex-1"><Keyboard className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-neutral-400" aria-hidden="true" /><input id={`manual-${label.replace(/\s+/g, '-').toLowerCase()}`} value={manual} onChange={(event) => setManual(event.target.value)} disabled={disabled} inputMode={manualInputMode} placeholder={manualPlaceholder} className={`${fieldClass} pl-10`} /></div>
        <button type="submit" disabled={disabled || !manualValid} className="min-h-11 rounded-xl bg-neutral-900 px-4 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-45 dark:bg-white dark:text-neutral-950">Use code</button>
      </form>
    </div>
  );
}
